import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent, SearchComponent, WmsDialogBodyComponent, WmsDialogComponent, WmsDialogFooterComponent, WmsDialogHeaderComponent, WmsMessageComponent, WmsTreeComponent } from '@wms/components';
import { FormsModule } from '@angular/forms';
import { DialogService, BindVariableService, FileService } from '@wms/services';
import { WMSFileUploadComponent } from '@wms/app/workspace/file-workspace/wms-file-upload/wms-file-upload.component';
import { environment } from "@wms/app";
import { forEach } from 'lodash-es';
import { Subscription } from 'rxjs';
@Component({
  selector: 'wms-bind-resource',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SearchComponent,
    WmsTreeComponent,
    WmsDialogComponent,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsMessageComponent,
    WMSFileUploadComponent
  ],
  templateUrl: './bind-resource.component.html',
  styleUrls: ['./bind-resource.component.less']
})
export class BindResourceComponent extends BaseComponent implements OnInit {

  showResourcesTab: boolean;
  resourcesInitialized: boolean;
  filteredResourceTree: any = [];
  resourcesTree: any = [];
  uploadMessage: any;
  dialogTitleKey: any;
  mimeType: any;
  resourceSearchKey: any;

  dialogService: DialogService;
  bindVariableService: BindVariableService;
  fileService: FileService;
  baseURL: string;

  /* model to display the resource type select */
  resourceTypes = [
    {
      "name": "Audio",
      "namekey": "LABEL_AUDIO"
    },
    {
      "name": "File",
      "namekey": "LABEL_FILE"
    },
    {
      "name": "Image",
      "namekey": "LABEL_IMAGE"
    },
    {
      "name": "Video",
      "namekey": "LABEL_VIDEO"
    }
  ];

  /* map which will contain the details of the resources by type */
  detailsByResourceType: any = {
    "Audio": {
      "path": "resources/audios",
      "data": null
    },
    "File": {
      "path": "resources/files",
      "data": null
    },
    "Image": {
      "path": "resources/images",
      "data": null
    },
    "Video": {
      "path": "resources/videos",
      "data": null
    }
  };

  constructor() {
    super();
    this.dialogService = inject(DialogService);
    this.fileService = inject(FileService);
    this.bindVariableService = inject(BindVariableService);
    this.baseURL = environment.SERVICES_BASE_PATH;
    this.initResources();
  }

  ngOnInit(): void {
  }

  searchResource($event: any){
    this.resourceSearchKey = $event.searchKey;
  }

  onResourceTypeChange(selectedResourceType: any) {
    this.loadResources(selectedResourceType);
  }

  updateMimeType(uploadMode: any) {
    /*Restricting file types to import based on uploadMode, i.e Import image option should show
    only image types, import jars should show only .jar files and import prefabs should show only .zip files*/
    switch (uploadMode) {
      case "images":
        this.mimeType = 'image/*';
        break;
      case "audios":
        this.mimeType = 'audio/*';
        break;
      case "videos":
        this.mimeType = 'video/*';
        break;
      case "files":
        this.mimeType = '';
        break;
      case "jars":
        this.mimeType = '.jar';
        break;
      case "resources":
        this.mimeType = '';
        break;
      case "project":
        this.mimeType = '';
        break;
      case "connectors":
        this.mimeType = '';
        break;
      default:
        this.mimeType = '.zip';
        break;
    }
  }

  openUploadDialog(dialogId: any, type: any) {
    let uploadMode = type.toLowerCase() + "s";
    this.updateMimeType(uploadMode);
    this.dialogService.open(dialogId);

  }

  onOk(event: any) {
    this.dialogService.close("uploadResourcesDialog");
  }

  onCancel(event: any) {
    this.dialogService.close("uploadResourcesDialog");
  }

  closeDialogWithId(id: string): void {
    this.dialogService.close(id)
  }

  /* initialize the app resources */
  initResources() {
    // if the app resources are already initialized. do nothing.
    if (this.resourcesInitialized) {
      return;
    }

    /* initialize the resources */
    /* flag used to delay the loading of resources.
     * load the resources only when the resources tab is selected.
     */

    this.resourcesInitialized = true;
    /* set the default resourceType */
    if (this.bindVariableService.isInBoundPath(this.detailsByResourceType.Audio.path)) {
      this.bindVariableService.selectedResourceType = "Audio";
    } else if (this.bindVariableService.isInBoundPath(this.detailsByResourceType.File.path)) {
      this.bindVariableService.selectedResourceType = "File";
    } else if (this.bindVariableService.isInBoundPath(this.detailsByResourceType.Video.path)) {
      this.bindVariableService.selectedResourceType = "Video";
    } else {
      this.bindVariableService.selectedResourceType = "Image";
    }
    // initialize the resources tree with the selected resource type.
    this.loadResources(this.bindVariableService.selectedResourceType);
  }

  /*
   * update the resource tree based on the selected resource type.
   */
  updateResourceTree(data: any) {
    this.resourcesTree = this.createResourceTreeObject(data);
    this.filteredResourceTree = this.resourcesTree;
    this.showResourcesTab = true;
  }

  /*
   * create resource tree object out of the given resource collection
   * @param resourceObject the collection of resources
   */
  createResourceTreeObject = (resourceObject: any) => {
    let resourceTreeObject = [],
      resourceFiles: any = [],
      folderClass,
      isTreeExpanded,
      isActive,
      nodeObj;

    if (!resourceObject) {
      return;
    }

    /* Iterating recursively over resource object to get a tree */
    switch (resourceObject.type) {
      case "folder":
        if (resourceObject.files.length > 0) {
          forEach(resourceObject.files, (fileResource: any) => {
            let isTreeExpandedFile,
              isActiveFile,
              nodeObjFile;
            if (fileResource.type === "file") {
              isTreeExpandedFile = this.bindVariableService.isInBoundPath(fileResource.path);
              isActiveFile = isTreeExpandedFile ? this.bindVariableService.isActiveEntityResource(fileResource.name) : false;
              nodeObjFile = {
                "id": fileResource.name,
                "class": "wms-file",
                "props": {
                  "name": fileResource.name,
                  "bindable": true,
                  "type": "resource",
                  "datatype": fileResource.type,
                  "path": fileResource.path
                },
                "collapsed": !isTreeExpandedFile
              };
              resourceFiles.push(nodeObjFile);
              if (isActiveFile) {
                this.bindVariableService.validateBinding(nodeObjFile.id, nodeObjFile.props);
              }
            } else if (fileResource.type === "folder") {
              let resourceTreeObject: any = (this.createResourceTreeObject(fileResource));
              resourceFiles.push(resourceTreeObject[0]);
            }
          });
        }
        isTreeExpanded = this.bindVariableService.isInBoundPath(resourceObject.path);
        isActive = isTreeExpanded ? this.bindVariableService.isActiveEntityResource(resourceObject.name) : false;
        nodeObj = {
          "id": resourceObject.name,
          "class": folderClass,
          "children": resourceFiles,
          "props": {
            "name": resourceObject.name,
            "bindable": true,
            "type": "resource",
            "datatype": resourceObject.type,
            "path": resourceObject.path
          },
          "collapsed": !isTreeExpanded,
          "active": isActive
        };
        resourceTreeObject.push(nodeObj);
        if (isActive) {
          this.bindVariableService.validateBinding(nodeObj.id, nodeObj.props);
        }
        break;
      case "file":
        resourceTreeObject.push({
          "id": resourceObject.name,
          "class": "file",
          "props": {
            "name": resourceObject.name,
            "bindable": true,
            "type": "resource",
            "datatype": resourceObject.type,
            "path": resourceObject.path
          }
        });
        break;
    }
    return resourceTreeObject;
  }

  /* load the resources based on the selected resourceType */
  loadResources(selectedResourceType: any, forceUpload?: any) {
    if (!selectedResourceType) {
      return;
    }

    this.bindVariableService.filetype = "";
    //$scope.wmvariables.resourcesFilter = '';
    this.bindVariableService.selectedResourceType = selectedResourceType;

    let details = this.detailsByResourceType[selectedResourceType];
    if (!forceUpload && details.data) {
      // details of the selected resources are available
      this.updateResourceTree(details.data);
    } else {
      // get the details from the server
      /* send request to get updated resources - if path is empty, it will fetch all
       resources under "src/main/webapp" folder */
      this.fileService.readDir(details.path).then((result: any) => {
        details.data = result;
        this.updateResourceTree(result);
      });
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }
}
